/**
 * Shared tree utilities for hierarchical items (rewards, punishments).
 */

export interface TreeItem {
  _key: string;
  parent_key?: string | null;
  sort_order?: number;
}

export interface TreeNode<T extends TreeItem> {
  item: T;
  children: TreeNode<T>[];
}

export interface FlattenedItem<T extends TreeItem> {
  item: T;
  depth: number;
  hasChildren: boolean;
  parentKey: string | null;
}

/**
 * Build a tree from a flat array of items using parent_key relationships.
 * Items involved in cycles are treated as roots (cycle is broken).
 */
export function buildTree<T extends TreeItem>(items: T[]): TreeNode<T>[] {
  const itemMap = new Map(items.map(i => [i._key, i]));

  // Detect items involved in cycles so we can break them
  const cycleKeys = new Set<string>();
  for (const item of items) {
    if (!item.parent_key) continue;
    const visited = new Set<string>();
    let key: string | null | undefined = item._key;
    while (key && itemMap.has(key)) {
      if (visited.has(key)) {
        // Everything in the visited set is part of a cycle
        for (const k of visited) cycleKeys.add(k);
        break;
      }
      visited.add(key);
      key = itemMap.get(key)!.parent_key;
    }
  }

  const childMap = new Map<string, TreeNode<T>[]>();

  for (const item of items) {
    // Break cycles: treat cyclic items as roots
    if (cycleKeys.has(item._key)) continue;
    if (item.parent_key && itemMap.has(item.parent_key) && !cycleKeys.has(item.parent_key)) {
      const siblings = childMap.get(item.parent_key) || [];
      siblings.push({ item, children: [] });
      childMap.set(item.parent_key, siblings);
    }
  }

  function attachChildren(nodes: TreeNode<T>[]) {
    for (const node of nodes) {
      node.children = childMap.get(node.item._key) || [];
      node.children.sort((a, b) => (a.item.sort_order ?? 0) - (b.item.sort_order ?? 0));
      attachChildren(node.children);
    }
  }

  const roots: TreeNode<T>[] = [];
  for (const item of items) {
    if (!item.parent_key || !itemMap.has(item.parent_key) || cycleKeys.has(item._key)) {
      roots.push({ item, children: [] });
    }
  }
  attachChildren(roots);

  // Sort roots and children by sort_order so optimistic updates reorder correctly
  roots.sort((a, b) => (a.item.sort_order ?? 0) - (b.item.sort_order ?? 0));

  return roots;
}

/**
 * Flatten a tree into a depth-annotated list, respecting collapsed state.
 * When activeId is provided, that node's children are auto-collapsed (for DnD).
 */
export function flattenTree<T extends TreeItem>(
  nodes: TreeNode<T>[],
  collapsedKeys: Set<string>,
  activeId?: string | null,
  depth = 0,
  parentKey: string | null = null,
): FlattenedItem<T>[] {
  const result: FlattenedItem<T>[] = [];
  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    result.push({ item: node.item, depth, hasChildren, parentKey });

    const isCollapsed = collapsedKeys.has(node.item._key) || node.item._key === activeId;
    if (hasChildren && !isCollapsed) {
      result.push(
        ...flattenTree(node.children, collapsedKeys, activeId, depth + 1, node.item._key),
      );
    }
  }
  return result;
}

/**
 * Get all descendant _key values of a given item.
 */
export function getDescendantKeys<T extends TreeItem>(items: T[], key: string): Set<string> {
  const result = new Set<string>();
  function collect(parentKey: string) {
    for (const item of items) {
      if (item.parent_key === parentKey && !result.has(item._key)) {
        result.add(item._key);
        collect(item._key);
      }
    }
  }
  collect(key);
  return result;
}

/**
 * Get the depth (nesting level) of an item in the tree.
 * Includes cycle detection to prevent infinite loops.
 */
export function getDepth<T extends TreeItem>(items: T[], item: T): number {
  let depth = 0;
  const itemMap = new Map(items.map(i => [i._key, i]));
  const visited = new Set<string>();
  let currentKey = item.parent_key;
  while (currentKey && itemMap.has(currentKey)) {
    if (visited.has(currentKey)) break; // cycle detected
    visited.add(currentKey);
    depth++;
    currentKey = itemMap.get(currentKey)!.parent_key;
  }
  return depth;
}

/**
 * Get all keys that are parents (have at least one child).
 */
export function getParentKeys<T extends TreeItem>(items: T[]): Set<string> {
  const itemMap = new Map(items.map(i => [i._key, i]));
  const parents = new Set<string>();
  for (const item of items) {
    if (item.parent_key && itemMap.has(item.parent_key)) {
      parents.add(item.parent_key);
    }
  }
  return parents;
}

/** Indent width in pixels for one tree level */
export const INDENT_PX = 24;

/**
 * Check if setting item's parent to candidateParentKey would create a cycle.
 * Returns true if the move is invalid (creates a cycle).
 */
export function wouldCreateCycle<T extends TreeItem>(
  items: T[],
  itemKey: string,
  candidateParentKey: string | null,
): boolean {
  if (!candidateParentKey) return false;
  if (candidateParentKey === itemKey) return true;

  // Walk up the ancestor chain from candidateParentKey;
  // if we find itemKey, it's a cycle.
  const itemMap = new Map(items.map(i => [i._key, i]));
  const visited = new Set<string>();
  let current = candidateParentKey;
  while (current) {
    if (current === itemKey) return true;
    if (visited.has(current)) return false; // existing cycle in data — stop
    visited.add(current);
    const parent = itemMap.get(current);
    if (!parent?.parent_key) break;
    current = parent.parent_key;
  }
  return false;
}

/**
 * Given the flattened items and drag state, determine projected depth + parent.
 */
export function getProjection<T extends TreeItem>(
  flatItems: FlattenedItem<T>[],
  activeId: string,
  overId: string,
  dragOffsetX: number,
  indentWidthPx = INDENT_PX,
): { depth: number; parentKey: string | null } {
  // Filter out active item for position calculations
  const itemsWithoutActive = flatItems.filter(i => i.item._key !== activeId);
  const overIndex = itemsWithoutActive.findIndex(i => i.item._key === overId);

  if (overIndex === -1) return { depth: 0, parentKey: null };

  const activeItem = flatItems.find(i => i.item._key === activeId);
  if (!activeItem) return { depth: 0, parentKey: null };

  const overItem = itemsWithoutActive[overIndex]!;

  // Max depth: can become child of over item
  const maxDepth = overItem.depth + 1;

  // Min depth: constrained by the next item below over
  let minDepth = 0;
  if (overIndex + 1 < itemsWithoutActive.length) {
    // We can't go shallower than the next item below, because
    // visually those items would appear to be our children
    minDepth = 0; // No real constraint — tree is re-rendered from parent_key
  }

  // Project depth from current depth + horizontal offset
  const depthOffset = Math.round(dragOffsetX / indentWidthPx);
  const projectedDepth = Math.max(minDepth, Math.min(maxDepth, activeItem.depth + depthOffset));

  // Find parent at projectedDepth - 1, walking backwards from over item
  let parentKey: string | null = null;
  if (projectedDepth > 0) {
    for (let i = overIndex; i >= 0; i--) {
      const fi = itemsWithoutActive[i]!;
      if (fi.depth === projectedDepth - 1) {
        parentKey = fi.item._key;
        break;
      }
      if (fi.depth < projectedDepth - 1) {
        break;
      }
    }
  }

  return { depth: projectedDepth, parentKey };
}

/**
 * Compute the reorder updates (batch of _key + parent_key + sort_order)
 * after a drag-and-drop operation.
 */
export function computeReorderUpdates<T extends TreeItem>(
  items: T[],
  movedItemKey: string,
  newParentKey: string | null,
  overId: string,
  flatItems: FlattenedItem<T>[],
): Array<{ _key: string; parent_key: string | null; sort_order: number }> {
  const movedItem = items.find(i => i._key === movedItemKey);
  if (!movedItem) return [];

  // Get current siblings under the new parent (excluding moved item)
  const newSiblings = items
    .filter(i => {
      if (i._key === movedItemKey) return false;
      const iParent = i.parent_key ?? null;
      return iParent === newParentKey;
    })
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // Determine where to insert among these siblings
  let insertIndex = 0;

  if (overId === newParentKey) {
    // Dropped directly on the parent → first child
    insertIndex = 0;
  } else {
    // Find over item's position among siblings
    const overSibIdx = newSiblings.findIndex(s => s._key === overId);
    if (overSibIdx !== -1) {
      insertIndex = overSibIdx + 1;
    } else {
      // Over item is not a sibling —
      // find the closest sibling before overId in flat order
      const flatWithoutActive = flatItems.filter(f => f.item._key !== movedItemKey);
      const overFlatIdx = flatWithoutActive.findIndex(f => f.item._key === overId);

      for (let i = overFlatIdx; i >= 0; i--) {
        const fi = flatWithoutActive[i]!;
        const sibIdx = newSiblings.findIndex(s => s._key === fi.item._key);
        if (sibIdx !== -1) {
          insertIndex = sibIdx + 1;
          break;
        }
      }
    }
  }

  // Insert moved item at computed position
  const ordered = [...newSiblings];
  ordered.splice(insertIndex, 0, movedItem);

  // Generate updates with sequential sort_order values
  const updates: Array<{ _key: string; parent_key: string | null; sort_order: number }> = [];
  for (let i = 0; i < ordered.length; i++) {
    const item = ordered[i]!;
    const isMovedItem = item._key === movedItemKey;
    const currentSortOrder = item.sort_order ?? 0;
    const currentParent = item.parent_key ?? null;

    if (isMovedItem || currentSortOrder !== i || currentParent !== (isMovedItem ? newParentKey : currentParent)) {
      updates.push({
        _key: item._key,
        parent_key: isMovedItem ? newParentKey : currentParent,
        sort_order: i,
      });
    }
  }

  return updates;
}

/* ─── Drop-spot DnD ──────────────────────────────────────────────── */

/** One valid depth option within a gap drop spot */
export interface DepthOption {
  /** Visual nesting depth */
  depth: number;
  /** Target parent key (null = root level) */
  parentKey: string | null;
  /** Insert index among target parent's children */
  sortOrder: number;
}

/** A single droppable gap between items, supporting multiple depth options */
export interface DropSpot {
  /** Unique ID for the droppable element */
  id: string;
  /** Gap index (-1 = before first item, i = after item[i]) */
  gapIndex: number;
  /** Shallowest valid depth */
  minDepth: number;
  /** Deepest valid depth */
  maxDepth: number;
  /** All valid depth options, sorted deepest-first */
  depthOptions: DepthOption[];
}

/**
 * Resolve which depth option to use based on the pointer's X coordinate.
 * The cursor X is converted to a depth level using the indent width,
 * then clamped to the spot's valid range.
 */
export function resolveDepthFromX(
  spot: DropSpot,
  pointerX: number,
  containerLeft: number,
  indentPx = INDENT_PX,
): DepthOption {
  // Convert pointer X to a depth value (each indent level = indentPx * 1.5rem factor)
  // We use the same rem-based indent as the items: depth * 1.5rem, capped at 6rem
  // For resolution we use a simpler linear mapping with INDENT_PX
  const relativeX = Math.max(0, pointerX - containerLeft);
  const rawDepth = Math.round(relativeX / indentPx);
  const clampedDepth = Math.max(spot.minDepth, Math.min(spot.maxDepth, rawDepth));

  // Find the matching depth option (or closest)
  const exact = spot.depthOptions.find(o => o.depth === clampedDepth);
  if (exact) return exact;

  // Fallback: find nearest valid depth
  let best = spot.depthOptions[0]!;
  let bestDist = Math.abs(best.depth - clampedDepth);
  for (const opt of spot.depthOptions) {
    const dist = Math.abs(opt.depth - clampedDepth);
    if (dist < bestDist) {
      best = opt;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Compute all valid drop spots for a drag operation.
 * Returns the visible items (excluding dragged item + descendants)
 * and one drop spot per gap, each containing multiple depth options.
 *
 * Gap index -1 = before the first visible item.
 * Gap index  i = after visibleItems[i].
 */
export function computeDropSpots<T extends TreeItem>(
  flatItems: FlattenedItem<T>[],
  activeId: string,
  allItems: T[],
): { visibleItems: FlattenedItem<T>[]; spotsByGap: Map<number, DropSpot> } {
  const descendantKeys = getDescendantKeys(allItems, activeId);

  // Remove dragged item and its descendants from the visible list
  const visible = flatItems.filter(
    fi => fi.item._key !== activeId && !descendantKeys.has(fi.item._key),
  );

  const spotsByGap = new Map<number, DropSpot>();

  for (let gapIdx = -1; gapIdx < visible.length; gapIdx++) {
    const itemAbove = gapIdx >= 0 ? visible[gapIdx]! : null;
    const itemBelow = gapIdx + 1 < visible.length ? visible[gapIdx + 1]! : null;

    const maxDepth = itemAbove ? itemAbove.depth + 1 : 0;
    const minDepth = itemBelow ? itemBelow.depth : 0;

    const depthOptions: DepthOption[] = [];

    for (let depth = maxDepth; depth >= minDepth; depth--) {
      // Determine parent for this depth level
      let parentKey: string | null = null;
      if (depth > 0) {
        for (let i = gapIdx; i >= 0; i--) {
          if (visible[i]!.depth === depth - 1) {
            parentKey = visible[i]!.item._key;
            break;
          }
          if (visible[i]!.depth < depth - 1) break;
        }
        if (!parentKey) continue;
      }

      // Prevent dropping into self / own descendants
      if (parentKey === activeId || (parentKey && descendantKeys.has(parentKey))) continue;

      // Compute insert index among siblings under parentKey
      const siblings = allItems.filter(
        i =>
          i._key !== activeId &&
          !descendantKeys.has(i._key) &&
          (i.parent_key ?? null) === parentKey,
      ).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

      let insertIdx = 0;
      let anySibVisible = false;
      for (const sib of siblings) {
        const sibFlatIdx = visible.findIndex(v => v.item._key === sib._key);
        if (sibFlatIdx !== -1) {
          anySibVisible = true;
          if (sibFlatIdx <= gapIdx) insertIdx++;
        }
      }
      // Collapsed parent → append after all existing children
      if (!anySibVisible && siblings.length > 0) {
        insertIdx = siblings.length;
      }

      depthOptions.push({ depth, parentKey, sortOrder: insertIdx });
    }

    if (depthOptions.length > 0) {
      const validMin = Math.min(...depthOptions.map(o => o.depth));
      const validMax = Math.max(...depthOptions.map(o => o.depth));
      spotsByGap.set(gapIdx, {
        id: `drop-gap|${gapIdx}`,
        gapIndex: gapIdx,
        minDepth: validMin,
        maxDepth: validMax,
        depthOptions,
      });
    }
  }

  return { visibleItems: visible, spotsByGap };
}

/**
 * Compute reorder updates from a drop-spot target (parentKey + insertIndex).
 */
export function computeDropSpotUpdates<T extends TreeItem>(
  items: T[],
  movedItemKey: string,
  targetParentKey: string | null,
  insertIndex: number,
): Array<{ _key: string; parent_key: string | null; sort_order: number }> {
  const movedItem = items.find(i => i._key === movedItemKey);
  if (!movedItem) return [];

  const siblings = items
    .filter(i => i._key !== movedItemKey && (i.parent_key ?? null) === targetParentKey)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const clamped = Math.min(insertIndex, siblings.length);
  const ordered = [...siblings];
  ordered.splice(clamped, 0, movedItem);

  const updates: Array<{ _key: string; parent_key: string | null; sort_order: number }> = [];
  for (let i = 0; i < ordered.length; i++) {
    const item = ordered[i]!;
    const isMovedItem = item._key === movedItemKey;
    const currentParent = item.parent_key ?? null;
    const newParent = isMovedItem ? targetParentKey : currentParent;

    if ((item.sort_order ?? 0) !== i || currentParent !== newParent) {
      updates.push({ _key: item._key, parent_key: newParent, sort_order: i });
    }
  }

  return updates;
}
